import { BomOutput, PartitionedBomGroup, StandardBomGroup, Version } from "fond/types";
import { formatUnit, metersToFeetDisplay } from "fond/utils";

type AnyBomGroup = StandardBomGroup | PartitionedBomGroup;

export interface ComparisonRuleValue {
  count: number;
  total: number;
}

export interface ComparisonRowData {
  // AG-Grid tree data path: [group.name, rule.id]
  path: string[];
  ruleId: string;
  description: string | null;
  unit: string | null;
  // Keyed by version ID, undefined if the version is missing this rule (or its whole BOM)
  values: Record<string, ComparisonRuleValue | undefined>;
  // True if this row represents the grand total row
  isGrandTotal?: boolean;
}

/**
 * Flatten each version's BOM into a single set of comparison rows.
 *
 * @param versions The versions being compared, in order of baseline first
 * @param boms The BOMs for each version, keyed by version ID
 * @param systemOfMeasurement The system of measurement to use ("metric" or "imperial")
 * @returns A flat list of comparison rows, one per rule in the baseline version's BOM, keyed by the baseline version's groups/rules.
 * Versions being compared are expected to share the same architecture, so group names and rule ids line up across all of them. A version
 * missing a rule (or its whole BOM) is left blank for that row. This implementation doesn't consider "partitions" in rules.
 */
export function buildComparisonRowData(versions: Version[], boms: Record<string, BomOutput>, systemOfMeasurement: string): ComparisonRowData[] {
  const baselineVersion = versions[0];
  const baselineBom = baselineVersion ? boms[baselineVersion.ID] : undefined;
  if (!baselineBom) return [];

  const rows: ComparisonRowData[] = [];

  baselineBom.groups.forEach((group) => {
    const groupsByVersion: Record<string, AnyBomGroup | undefined> = {};
    versions.forEach((version) => {
      groupsByVersion[version.ID] = boms[version.ID]?.groups.find((candidate) => candidate.name === group.name);
    });

    group.ruleOutput
      .filter((rule) => Number(rule.count) > 0)
      .forEach((rule) => {
        const unitConversionRequired = rule.unit === "meters" && systemOfMeasurement === "imperial";
        const values: Record<string, ComparisonRuleValue | undefined> = {};

        versions.forEach((version) => {
          const matchedRule = groupsByVersion[version.ID]?.ruleOutput.find((candidate) => candidate.id === rule.id);
          values[version.ID] = matchedRule
            ? {
                count: unitConversionRequired ? metersToFeetDisplay(Number(matchedRule.count)) : Number(matchedRule.count),
                total: Number(matchedRule.total),
              }
            : undefined;
        });

        rows.push({
          path: [group.name, rule.id],
          ruleId: rule.id,
          description: rule.description,
          unit: unitConversionRequired ? formatUnit(systemOfMeasurement) : rule.unit,
          values,
        });
      });
  });

  return rows;
}

/**
 * Builds the pinned "Overall cost" footer row from each version's BOM total
 *
 * @param versions The versions being compared, in order of baseline first
 * @param boms The BOMs for each version, keyed by version ID
 * @returns A single row with the total cost for each version
 *
 */
export function buildGrandTotalRow(versions: Version[], boms: Record<string, BomOutput>): ComparisonRowData & { isGrandTotal: true } {
  const values: Record<string, ComparisonRuleValue | undefined> = {};
  versions.forEach((version) => {
    const total = boms[version.ID]?.total;
    values[version.ID] = total !== undefined ? { count: 0, total } : undefined;
  });

  return {
    path: [],
    ruleId: "",
    description: null,
    unit: null,
    values,
    isGrandTotal: true,
  };
}
